import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api, imgUrl } from "@/lib/api";
import { useSite } from "@/lib/useSite";

const EASE = [0.16, 1, 0.3, 1];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: site } = useSite();
  const [photoIdx, setPhotoIdx] = useState(0);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => (await api.get(`/products/${slug}`)).data,
  });

  const phone = site?.settings?.phone || "6366634517";
  const products = site?.products || [];
  const idx = products.findIndex((p) => p.slug === slug);
  const next = idx >= 0 ? products[(idx + 1) % products.length] : null;
  const photos = product?.photos?.length ? product.photos : [];

  const enquire = () => {
    sessionStorage.setItem("srs_enquiry_product", product.title);
    navigate("/#contact");
  };

  return (
    <div data-testid="product-detail-page" className="min-h-screen bg-white">
      <Nav solid />
      <main className="mx-auto max-w-[1400px] px-5 pb-24 pt-28 sm:px-8 sm:pt-32">
        <Link
          to="/"
          data-testid="product-back-link"
          className="mb-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-mute transition-colors hover:text-srs"
        >
          <ArrowLeft size={14} /> Back to the range
        </Link>

        {isLoading && (
          <div className="flex h-96 items-center justify-center">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-subtle">Loading…</span>
          </div>
        )}

        {isError && (
          <div data-testid="product-not-found" className="flex h-96 flex-col items-center justify-center gap-4">
            <h1 className="font-display text-4xl font-bold tracking-tight">Product not found</h1>
            <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.22em] text-srs">Return home</Link>
          </div>
        )}

        {product && (
          <>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-subtle">
              SRS / Range / <span className="text-srs">{String(idx + 1).padStart(2, "0")}</span>
            </p>
            <h1
              data-testid="product-detail-title"
              className="max-w-4xl overflow-hidden font-display text-4xl font-medium leading-[1.0] tracking-tight text-ink sm:text-6xl"
            >
              <motion.span className="block" initial={{ y: "60%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, ease: EASE }}>
                {product.title}
              </motion.span>
            </h1>
            {product.tagline && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-subtle"
              >
                {product.tagline}
              </motion.p>
            )}

            <div className="mt-12 grid gap-12 lg:grid-cols-12">
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                className="lg:col-span-7"
              >
                {photos.length > 0 && (
                  <>
                    <div className="overflow-hidden rounded-3xl bg-bone shadow-[0_24px_60px_-28px_rgba(13,13,17,0.35)]" data-testid="product-detail-modal">
                      <motion.img
                        key={photoIdx}
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        src={imgUrl(photos[photoIdx])}
                        alt={product.title}
                        className="aspect-[4/3] w-full object-cover"
                      />
                    </div>
                    {photos.length > 1 && (
                      <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                        {photos.map((ph, i) => (
                          <button
                            key={i}
                            data-testid={`product-photo-thumb-${i}`}
                            onClick={() => setPhotoIdx(i)}
                            className={`h-20 w-28 shrink-0 overflow-hidden rounded-xl transition-all ${i === photoIdx ? "ring-2 ring-srs ring-offset-2" : "opacity-50 hover:opacity-100"}`}
                          >
                            <img src={imgUrl(ph)} alt="" loading="lazy" className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {product.description && (
                  <p data-testid="product-description" className="mt-10 max-w-2xl text-base leading-relaxed text-mute sm:text-lg">
                    {product.description}
                  </p>
                )}
              </motion.div>

              <motion.aside
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                className="lg:col-span-5"
              >
                {product.specs?.length > 0 && (
                  <div className="overflow-hidden rounded-3xl border border-line">
                    <p className="border-b border-line bg-bone px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-subtle">
                      Specifications
                    </p>
                    <ul data-testid="product-specs-list">
                      {product.specs.map((spec, i) => (
                        <li key={i} className="flex gap-4 border-b border-line px-6 py-4 last:border-b-0">
                          <span className="font-mono text-[11px] font-semibold text-srs">{String(i + 1).padStart(2, "0")}</span>
                          <span className="font-mono text-[13px] leading-relaxed text-ink">{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-6 grid gap-3">
                  <button
                    data-testid="product-enquire-button"
                    onClick={enquire}
                    className="w-full rounded-full bg-srs px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-srs-dark"
                  >
                    Enquire about this product
                  </button>
                  <a
                    data-testid="product-call-button"
                    href={`tel:+91${phone}`}
                    className="flex w-full items-center justify-center gap-3 rounded-full border border-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-ink hover:text-white"
                  >
                    <Phone size={14} /> Call {phone}
                  </a>
                </div>
              </motion.aside>
            </div>

            {next && (
              <Link
                to={`/products/${next.slug}`}
                data-testid="product-next-link"
                className="group mt-24 flex items-center justify-between border-t border-line pt-10"
              >
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-subtle">Next in the range</p>
                  <p className="mt-2 font-display text-3xl font-bold tracking-tight text-ink transition-colors group-hover:text-srs sm:text-5xl">
                    {next.title}
                  </p>
                </div>
                <ArrowRight size={36} className="shrink-0 text-ink transition-all duration-300 group-hover:translate-x-2 group-hover:text-srs" />
              </Link>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
